import { useEffect, useRef, useState } from "react";
import {
    Box, Flex, Text, Input, InputGroup, InputLeftElement, Select, Checkbox,
    Popover, PopoverTrigger, PopoverContent, PopoverBody, useDisclosure, Spinner, Button,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { CustomTable, CustomToast, AlertCard } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";
import { Column } from "../../../../components/CustomTable/customTable";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import MyDiv from "./EmployeeWiseAttendance.style";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

type DayRow = {
    date: string;
    day: string;
    inTime: string;
    outTime: string;
    status: number | null;
    statusName: string;
    isFinalized: boolean;
    isWeekOff: boolean;
};

const EmployeeWiseAttendance = () => {
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);

    const now = new Date();
    const [year, setYear] = useState<number>(now.getFullYear());
    const [month, setMonth] = useState<number>(now.getMonth() + 1);

    const [days, setDays] = useState<DayRow[]>([]);
    const [defaultInTime, setDefaultInTime] = useState("");
    const [defaultOutTime, setDefaultOutTime] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveAllOpen, setSaveAllOpen] = useState(false);

    // Track original values to detect individual row modifications
    const originalDaysRef = useRef<Record<string, { inTime: string; outTime: string; isWeekOff: boolean }>>({});
    const currentDaysRef = useRef<DayRow[]>([]);
    currentDaysRef.current = days;

    // Single-row update confirmation state
    const [singleAlertOpen, setSingleAlertOpen] = useState(false);
    const [pendingRowUpdate, setPendingRowUpdate] = useState<DayRow | null>(null);
    const [updatingSingle, setUpdatingSingle] = useState(false);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

    // ---- Future-date guards ----
    const isFutureMonth = (y: number, m: number) => {
        const d = new Date(y, m - 1, 1);
        const cur = new Date(now.getFullYear(), now.getMonth(), 1);
        return d > cur;
    };

    // Blocks TODAY and anything after today. Past dates (including every
    // date in a past month) are never blocked, since they'll always be < today.
    const isFutureDate = (dateStr: string) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        d.setHours(0, 0, 0, 0);
        return d >= today;
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!searchTerm.trim()) {
            setSearchResults([]);
            onClose();
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            const data = await fetchApi("Hrlense_Attendance/searchEmployee", "GET", null, `searchTerm=${encodeURIComponent(searchTerm)}`);
            setSearching(false);
            if (data) {
                setSearchResults(data);
                onOpen();
            }
        }, 300);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchTerm]);

    useEffect(() => {
        if (selectedEmployee) fetchMonthData();
    }, [selectedEmployee, year, month]);

    const fetchMonthData = async () => {
        const data = await fetchApi(
            "Hrlense_Attendance/employeeWiseAttendance",
            "GET",
            null,
            `employeeId=${selectedEmployee.id}&year=${year}&month=${month}`
        );
        if (data) {
            const mappedDays: DayRow[] = (data.days || []).map((d: any) => ({
                date: d.date,
                day: d.day,
                inTime: d.inTime || "",
                outTime: d.outTime || "",
                status: d.status,
                statusName: d.statusName,
                isFinalized: d.isFinalized,
                isWeekOff: d.isWeekOff || false,
            }));

            setDays(mappedDays);
            currentDaysRef.current = mappedDays;

            // Record initial state for change detection
            const map: Record<string, { inTime: string; outTime: string; isWeekOff: boolean }> = {};
            mappedDays.forEach((d) => {
                map[d.date] = { inTime: d.inTime, outTime: d.outTime, isWeekOff: d.isWeekOff };
            });
            originalDaysRef.current = map;

            if (data.shiftStart) setDefaultInTime(data.shiftStart);
            if (data.shiftEnd) setDefaultOutTime(data.shiftEnd);
        }
    };

    const handleSelectEmployee = (emp: any) => {
        setSelectedEmployee(emp);
        setSearchTerm(`${emp.emp_Name} (${emp.user_Employee_id})`);
        setSearchResults([]);
        onClose();
    };

    // Update a single row's in/out time or week-off flag locally
    const updateRow = (date: string, patch: Partial<DayRow>) => {
        if (isFutureDate(date)) return; // guard against programmatic edits on today/future rows
        currentDaysRef.current = currentDaysRef.current.map((d) => (d.date === date ? { ...d, ...patch } : d));
        setDays((prev) => prev.map((d) => (d.date === date ? { ...d, ...patch } : d)));
    };

    // On blur of punch in or out: detect change and prompt confirmation modal
    const handleTimeBlur = (e: React.FocusEvent<HTMLDivElement>, date: string) => {
        const related = e.relatedTarget as HTMLElement | null;
        // If focus moved within the same date's inputs (e.g. between In-Time and Out-Time), do not trigger alert yet
        if (related && related.closest(`[data-row-date="${date}"]`)) {
            return;
        }

        const latestRow = currentDaysRef.current.find((d) => d.date === date);
        const orig = originalDaysRef.current[date];
        if (!latestRow || !orig) return;

        // Check if punch in or punch out actually changed
        const hasChanged = latestRow.inTime !== orig.inTime || latestRow.outTime !== orig.outTime;
        if (hasChanged && !singleAlertOpen && !updatingSingle) {
            setPendingRowUpdate(latestRow);
            setSingleAlertOpen(true);
        }
    };

    // Confirm single date attendance update
    const handleConfirmSingleUpdate = async () => {
        if (!selectedEmployee || !pendingRowUpdate) return;
        try {
            setUpdatingSingle(true);
            const payload = {
                employeeId: selectedEmployee.id,
                attendanceDate: pendingRowUpdate.date,
                inTime: pendingRowUpdate.isWeekOff ? "" : pendingRowUpdate.inTime,
                outTime: pendingRowUpdate.isWeekOff ? "" : pendingRowUpdate.outTime,
                isWeekOff: pendingRowUpdate.isWeekOff,
            };
            const response = await fetchApi("Hrlense_Attendance/Update", "PUT", payload);
            if (response) {
                addToast({
                    message: response.message || `Attendance updated for ${pendingRowUpdate.date}`,
                    status: "success",
                });
                // Update local baseline ref
                originalDaysRef.current[pendingRowUpdate.date] = {
                    inTime: pendingRowUpdate.inTime,
                    outTime: pendingRowUpdate.outTime,
                    isWeekOff: pendingRowUpdate.isWeekOff,
                };
                // Refetch month data to ensure status badge, shift, and work hours reflect new calculations
                fetchMonthData();
            }
        } catch (err: any) {
            addToast({ message: err.message || "Failed to update attendance", status: "error" });
        } finally {
            setUpdatingSingle(false);
            setSingleAlertOpen(false);
            setPendingRowUpdate(null);
        }
    };

    // Cancel single update -> revert to original
    const handleCancelSingleUpdate = () => {
        if (pendingRowUpdate) {
            const orig = originalDaysRef.current[pendingRowUpdate.date];
            if (orig) {
                currentDaysRef.current = currentDaysRef.current.map((d) =>
                    d.date === pendingRowUpdate.date ? { ...d, inTime: orig.inTime, outTime: orig.outTime } : d
                );
                setDays((prev) =>
                    prev.map((d) =>
                        d.date === pendingRowUpdate.date ? { ...d, inTime: orig.inTime, outTime: orig.outTime } : d
                    )
                );
            }
        }
        setSingleAlertOpen(false);
        setPendingRowUpdate(null);
    };

    // Fill every non-week-off, non-today/future row with the default in/out times
    const handleFillDefaults = () => {
        const updated = days.map((d) =>
            d.isWeekOff || isFutureDate(d.date)
                ? d
                : { ...d, inTime: defaultInTime, outTime: defaultOutTime }
        );
        currentDaysRef.current = updated;
        setDays(updated);
        addToast({ message: "Default times applied to all working days", status: "success" });
    };

    const handleSaveAll = async () => {
        if (!selectedEmployee) return;
        try {
            setSaving(true);
            const payload = {
                employeeId: selectedEmployee.id,
                days: days
                    .filter((d) => !isFutureDate(d.date)) // never send today/future-dated entries
                    .map((d) => ({
                        date: d.date,
                        inTime: d.isWeekOff ? "" : d.inTime,
                        outTime: d.isWeekOff ? "" : d.outTime,
                        isWeekOff: d.isWeekOff,
                    })),
            };
            const response = await fetchApi("Hrlense_Attendance/employeeWiseAttendance/bulkUpdate", "POST", payload);
            if (response) {
                addToast({ message: "Month's attendance saved successfully", status: "success" });
                fetchMonthData();
            }
        } catch (err: any) {
            addToast({ message: err.message || "Something went wrong", status: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <MyDiv>
            <Flex gap={4} mb={4} align="center" wrap="wrap">
                <Popover isOpen={isOpen} onClose={onClose} autoFocus={false} placement="bottom-start" matchWidth>
                    <PopoverTrigger>
                        <InputGroup maxW="280px">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search employee by name"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setSelectedEmployee(null);
                                }}
                                onFocus={() => searchResults.length > 0 && onOpen()}
                            />
                        </InputGroup>
                    </PopoverTrigger>
                    <PopoverContent w="280px">
                        <PopoverBody p={1} maxH="240px" overflowY="auto">
                            {searching ? (
                                <Flex justify="center" py={2}><Spinner size="sm" /></Flex>
                            ) : searchResults.length > 0 ? (
                                searchResults.map((emp) => (
                                    <Box key={emp.id} px={3} py={2} borderRadius="md" cursor="pointer" _hover={{ bg: "gray.100" }} onClick={() => handleSelectEmployee(emp)}>
                                        <Text className="font-poppins text_sm text_medium">{emp.emp_Name}</Text>
                                        <Text className="font-poppins text_xs" color="gray.500">{emp.user_Employee_id} · {emp.company_Name}</Text>
                                    </Box>
                                ))
                            ) : (
                                <Text px={3} py={2} className="font-poppins text_sm" color="gray.500">No matches</Text>
                            )}
                        </PopoverBody>
                    </PopoverContent>
                </Popover>

                <Select
                    maxW="140px"
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    isDisabled={!selectedEmployee}
                >
                    {MONTHS.map((m, i) => (
                        <option key={m} value={i + 1} disabled={isFutureMonth(year, i + 1)}>
                            {m}
                        </option>
                    ))}
                </Select>

                <Select
                    maxW="110px"
                    value={year}
                    onChange={(e) => {
                        const newYear = Number(e.target.value);
                        setYear(newYear);
                        if (isFutureMonth(newYear, month)) setMonth(now.getMonth() + 1);
                    }}
                    isDisabled={!selectedEmployee}
                >
                    {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </Select>
            </Flex>

            {selectedEmployee && (
                <Flex gap={4} mb={4} align="center" wrap="wrap" p={3} bg="gray.50" borderRadius="md">
                    <Text className="font-poppins text_sm text_medium">Default Time:</Text>
                    <Box maxW="120px">
                        <TimePicker onChange={(v) => setDefaultInTime(v || "")} value={defaultInTime} disableClock format="HH:mm" clearIcon={null} clockIcon={null} />
                    </Box>
                    <Text className="font-poppins text_sm">to</Text>
                    <Box maxW="120px">
                        <TimePicker onChange={(v) => setDefaultOutTime(v || "")} value={defaultOutTime} disableClock format="HH:mm" clearIcon={null} clockIcon={null} />
                    </Box>
                    <Button size="sm" variant="outline" onClick={handleFillDefaults}>
                        Fill All Working Days
                    </Button>
                    <Button
                        size="sm"
                        bg="#006838" color="#fff" _hover={{ bg: "#005529" }}
                        onClick={() => setSaveAllOpen(true)}
                        isLoading={saving}
                        ml="auto"
                    >
                        Save All
                    </Button>
                </Flex>
            )}

            {selectedEmployee ? (
                <CustomTable value={days} emptyMessage={loading ? "Loading..." : "No Data Available"}>
                    <Column header="Date" field="date" />
                    <Column header="Day" field="day" />
                    <Column
                        header="Week Off"
                        body={(row: DayRow) => (
                            <Checkbox
                                isChecked={row.isWeekOff}
                                isDisabled={isFutureDate(row.date)}
                                onChange={(e) => updateRow(row.date, { isWeekOff: e.target.checked })}
                            />
                        )}
                    />
                    <Column header="In-Time" body={(row: DayRow) => {
                        const disabled = row.isWeekOff || isFutureDate(row.date);
                        return (
                            <Box
                                maxW="120px"
                                data-row-date={row.date}
                                opacity={disabled ? 0.4 : 1}
                                pointerEvents={disabled ? "none" : "auto"}
                                onBlur={(e) => handleTimeBlur(e, row.date)}
                            >
                                <TimePicker
                                    onChange={(value: string | null) => updateRow(row.date, { inTime: value || "" })}
                                    value={row.inTime}
                                    disableClock
                                    format="HH:mm"
                                    clearIcon={null}
                                    clockIcon={null}
                                />
                            </Box>
                        );
                    }}
                    />
                    <Column header="Out-Time" body={(row: DayRow) => {
                        const disabled = row.isWeekOff || isFutureDate(row.date);
                        return (
                            <Box
                                maxW="120px"
                                data-row-date={row.date}
                                opacity={disabled ? 0.4 : 1}
                                pointerEvents={disabled ? "none" : "auto"}
                                onBlur={(e) => handleTimeBlur(e, row.date)}
                            >
                                <TimePicker
                                    onChange={(value: string | null) => updateRow(row.date, { outTime: value || "" })}
                                    value={row.outTime}
                                    disableClock
                                    format="HH:mm"
                                    clearIcon={null}
                                    clockIcon={null}
                                />
                            </Box>
                        );
                    }}
                    />
                    <Column header="Status" body={(row: any) => {
                        const status = row.statusName?.toLowerCase().trim() || "";
                        const statusStyles: Record<string, { bg: string; color: string }> = {
                            present: { bg: "#dcfce7", color: "#166534" },
                            absent: { bg: "#fee2e2", color: "#991b1b" },
                            "half day": { bg: "#fef3c7", color: "#92400e" },
                            late: { bg: "#fef9c3", color: "#854d0e" },
                            earlyout: { bg: "#e0f2fe", color: "#075985" },
                            punchinmissing: { bg: "#f3e8ff", color: "#6b21a8" },
                            "punch in missing": { bg: "#f3e8ff", color: "#6b21a8" },
                            punchoutmissing: { bg: "#fae8ff", color: "#86198f" },
                            "punch out missing": { bg: "#fae8ff", color: "#86198f" },
                            "week off": { bg: "#f0fdf4", color: "#166534" },
                            holiday: { bg: "#e0e7ff", color: "#3730a3" },
                            "short leave": { bg: "#ccfbf1", color: "#115e59" },
                            "short leave half day": { bg: "#fed7aa", color: "#9a3412" },
                        };
                        const { bg, color } = statusStyles[status] || {
                            bg: "#f1f5f9",
                            color: "#475569",
                        };

                        return (
                            <Box px={3} py={1} borderRadius="md" textAlign="center" fontSize="sm" fontWeight="500" bg={bg} color={color} className="font-poppins">
                                {row.statusName || "—"}
                            </Box>
                        );
                    }}
                    />
                </CustomTable>
            ) : (
                <Text className="font-poppins text_sm" color="gray.500">
                    Search and select an employee to view attendance.
                </Text>
            )}

            {/* Confirmation alert for single-date inline punch edit */}
            <AlertCard
                isOpen={singleAlertOpen}
                onClose={handleCancelSingleUpdate}
                onConfirm={handleConfirmSingleUpdate}
                title="Mark Attendance"
                description={`Are you sure you want to update attendance for ${pendingRowUpdate?.date} (${pendingRowUpdate?.day})? In-Time: ${pendingRowUpdate?.inTime || "—"}, Out-Time: ${pendingRowUpdate?.outTime || "—"}.`}
                confirmLabel="Yes, Update"
                colorScheme="green"
            />

            {/* Confirmation alert for bulk save */}
            <AlertCard
                isOpen={saveAllOpen}
                onClose={() => setSaveAllOpen(false)}
                onConfirm={() => { setSaveAllOpen(false); handleSaveAll(); }}
                title="Save Month's Attendance"
                description={`Are you sure you want to save attendance for ${selectedEmployee?.emp_Name} for ${MONTHS[month - 1]} ${year}? This will overwrite existing records for the month.`}
                confirmLabel="Yes, Save All"
                colorScheme="green"
            />
        </MyDiv>
    );
};

export default EmployeeWiseAttendance;
